import {Component, OnInit} from '@angular/core';
import {Observable} from 'rxjs';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {BankInteractor} from '../../../interactors/bank.interactor';
import {map, startWith} from 'rxjs/operators';
import {BankDetailsDirective} from '../bank-details.directive';

@Component({
  selector: 'app-bank-account-details-by-name',
  templateUrl: './bank-account-details-by-name.component.html',
  styleUrls: ['./bank-account-details-by-name.component.css']
})
export class BankAccountDetailsByNameComponent extends BankDetailsDirective implements OnInit {
  public bankList: string[];
  public cities: string[];
  public bankBranches: string[];
  filteredBankBranches: string[];
  public filteredCities: Observable<string[]>;
  public filteredBankNames: Observable<string[]>;
  loadingCities: boolean;
  loadingBranches: boolean;

  constructor(formBuilder: FormBuilder, protected bankInteractor: BankInteractor) {
    super();

    this.searchBankFormGroup = formBuilder.group({
      ifscCode: new FormControl({value: null, disabled: true}),
      bankName: new FormControl(null, Validators.required),
      bankCity: new FormControl(null, Validators.required),
      bankBranchName: new FormControl(null, Validators.required)
    });
  }

  async ngOnInit(): Promise<void> {
    this.bankList = await this.bankInteractor.getBankNames();
    this.filteredBankNames =
        this.searchBankFormGroup.controls.bankName.valueChanges
            .pipe(startWith(''), map(value => this.filterBankNames(value)));

    this.searchBankFormGroup.controls.bankBranchName.valueChanges.subscribe(value => {
      this.filterBankBranchNames(value);
    });
  }

  protected prepopulate(): void {
    if (this.bankAccount) {
      this.searchBankFormGroup.controls.bankBranchName.disable();
      this.searchBankFormGroup.controls.bankCity.disable();
    }
  }

  async onBankNameSelected(): Promise<void> {
      this.clearFieldsForBankName();
      const selectedBankName = this.searchBankFormGroup.controls.bankName.value;
      this.loadingCities = true;
      this.cities = await this.bankInteractor.getCitiesbyBankName(selectedBankName);
      this.loadingCities = false;
      this.searchBankFormGroup.controls.bankBranchName.enable();
      this.searchBankFormGroup.controls.bankCity.enable();
  }

  async onCitySelected(): Promise<void> {
    this.clearFieldsForBankCity();
    const selectedBankName = this.searchBankFormGroup.controls.bankName.value;
    const selectedCityName = this.searchBankFormGroup.controls.bankCity.value;
    this.loadingBranches = true;
    this.bankBranches = await this.bankInteractor.getBankBranches(selectedBankName, selectedCityName);
    this.loadingBranches = false;
    this.filteredBankBranches = this.bankBranches;
  }
  public getCityName() {
    const citySearch = this.searchBankFormGroup.controls.bankCity.value;

    if (citySearch?.length < 3) {
      this.filteredCities = null;
    }
    if (citySearch?.length === 3) {
      this.filteredCities = this.searchBankFormGroup.controls.bankCity.valueChanges
          .pipe(startWith(citySearch), map(value => this.filterCityNames(value)));
    }
  }

  validateBankName() {
    const bankName = this.searchBankFormGroup.controls.bankName.value;
    const isBankPresent = !!this.bankList.find((bank) => bank === bankName);
    if (!isBankPresent) {
      this.searchBankFormGroup.controls.bankName.setErrors({'invalidBankName': true});
      this.clearFieldsForBankName();
    }
  }

  validateBankCity() {
    const bankCity = this.searchBankFormGroup.controls.bankCity.value;
    const isBankCityPresent = !!this.cities.find((city) => city === bankCity);
    if (!isBankCityPresent) {
      this.searchBankFormGroup.controls.bankCity.setErrors({'invalidBankCity': true});
      this.clearFieldsForBankCity();
    }
  }

  validateBankBranch() {
    const bankBranchName = this.searchBankFormGroup.controls.bankBranchName.value;
    const isBankBranchPresent = !!this.bankBranches.find((branch) => branch === bankBranchName);
    if (!isBankBranchPresent) {
      this.searchBankFormGroup.controls.bankBranchName.setErrors({'invalidBankBranch': true});
      this.clearFieldsForBankBranch();
    }
  }

  async onBankBranchSelected(): Promise<void> {
    const bankBranchName = this.searchBankFormGroup.controls.bankBranchName.value;
    const isBankBranchPresent = !!this.bankBranches.find((branch) => branch === bankBranchName);
    if (!isBankBranchPresent) {
      this.searchBankFormGroup.controls.bankBranchName.setErrors({'invalidBankBranch': true});
      this.clearFieldsForBankBranch();
    } else {
      this.clearFieldsForBankBranch();
      const selectedBankName = this.searchBankFormGroup.controls.bankName.value;
      const selectedBranchName = this.searchBankFormGroup.controls.bankBranchName.value;

      const ifscCode = await this.bankInteractor.getIfsc(selectedBranchName, selectedBankName);
      this.searchBankFormGroup.controls.ifscCode.setValue(ifscCode);
    }
  }

  protected clearFieldsForBankName(): void {
    this.searchBankFormGroup.controls.bankCity.reset();
    this.clearFieldsForBankCity();
  }

  protected clearFieldsForBankCity(): void {
    this.searchBankFormGroup.controls.bankBranchName.reset();
    this.clearFieldsForBankBranch();
  }

  protected clearFieldsForBankBranch(): void {
    this.searchBankFormGroup.controls.ifscCode.reset();
  }

  private filterBankNames(val: string): string[] {
    if (val) {
      return this.bankList.filter(bankName => bankName.toLowerCase().startsWith(val.toLowerCase()));
    }
    return [];
  }

  private filterCityNames(val: string): string[] {
    if (val) {
      return this.cities.filter(city => city.toLowerCase().startsWith(val.toLowerCase()));
    }
    return [];
  }

  private filterBankBranchNames(val: string): void {
    if (val) {
      this.filteredBankBranches = this.bankBranches.filter(branch => branch.toLowerCase().startsWith(val.toLowerCase()));
    } else {
      this.filteredBankBranches = this.bankBranches;
    }
  }
}
